import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';

interface User {
  id: string;
  email?: string;
  phone?: string;
  firstName: string;
  lastName: string;
  role: string;
  businessName?: string;
  city?: string;
}

interface AuthContextType {
  user: User | null;
  admin: User | null; // Kept for backward compatibility
  isAuthenticated: boolean;
  isLoading: boolean;
  isVendor: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if token exists
    const token = localStorage.getItem('adminToken');
    const adminData = localStorage.getItem('adminData');

    if (token && adminData) {
      setUser(JSON.parse(adminData));
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await api.login(email, password);

      // Allow both admin and vendor roles
      if (response.user.role !== 'admin' && response.user.role !== 'vendor') {
        throw new Error('Access denied. Admin or Vendor role required.');
      }

      localStorage.setItem('adminToken', response.token);
      localStorage.setItem('adminData', JSON.stringify(response.user));
      setUser(response.user);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    setUser(null);
    window.location.href = '/login';
  };

  const value: AuthContextType = {
    user,
    admin: user, // Backward compatibility
    isAuthenticated: !!user,
    isLoading,
    isVendor: user?.role === 'vendor',
    isAdmin: user?.role === 'admin',
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
